import { Component } from '@theme/component';

/**
 * Discount Popup Web Component
 *
 * Manages a floating trigger button and a dismissible modal popup for capturing
 * customer email addresses in exchange for a discount code.
 *
 * Attributes:
 *  - auto-open-delay   {number} Seconds before auto-showing the dialog (default: 5)
 *  - cookie-days       {number} Days to suppress auto-open after the user dismisses (default: 30)
 *  - cookie-name       {string} Name of the dismiss/subscribed cookie
 *  - disable-auto-open {boolean} Presence disables the auto-open timer entirely
 *  - show-success      {boolean} Presence opens the dialog in success state on connect
 *                                (set by Liquid when ?kic_popup=subscribed is present in URL)
 *
 * @typedef {object} DiscountPopupRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLDivElement}    formFields
 * @property {HTMLDivElement}    successState
 * @property {HTMLParagraphElement} errorMessage
 * @property {HTMLButtonElement} submitButton
 *
 * @extends Component<DiscountPopupRefs>
 */
export class DiscountPopupComponent extends Component {

  /** @type {string} */
  #cookieName = 'kic_discount_popup';

  /** @type {number} */
  #autoOpenDelay = 5000;

  /** @type {number} */
  #cookieDays = 30;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #autoOpenTimer = null;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#cookieName = this.getAttribute('cookie-name') ?? 'kic_discount_popup';
    this.#autoOpenDelay = Number(this.getAttribute('auto-open-delay') ?? '5') * 1000;
    this.#cookieDays = Number(this.getAttribute('cookie-days') ?? '30');

    // Temporarily prevent form submission
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.#handleFormSubmit, { capture: true });
    }

    // Page reloaded after successful form submission — open in success state and set long-lived cookie
    if (this.hasAttribute('show-success')) {
      this.#setCookie(365);
      this.#showDialog();
      // Clean the query param from the URL so refreshes don't re-trigger
      const cleanUrl = window.location.pathname + window.location.hash;
      history.replaceState(null, '', cleanUrl);
      return;
    }

    // Auto-open after delay unless disabled or cookie is set
    if (!this.hasAttribute('disable-auto-open') && !this.#hasCookie()) {
      this.#autoOpenTimer = setTimeout(() => this.#showDialog(), this.#autoOpenDelay);
    }
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (
      this.refs.dialog ?? this.querySelector('dialog[ref="dialog"]')
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#autoOpenTimer !== null) {
      clearTimeout(this.#autoOpenTimer);
      this.#autoOpenTimer = null;
    }

    const dialog = this.#dialog;
    if (dialog) {
      dialog.removeEventListener('click', this.#handleBackdropClick);
      dialog.removeEventListener('keydown', this.#handleEscapeKey);
    }

    const form = this.querySelector('form');
    if (form) {
      form.removeEventListener('submit', this.#handleFormSubmit, { capture: true });
    }
  }

  // ─── Private event handlers ────────────────────────────────────────────────

  /**
   * Temporarily blocks form submission and shows an alert.
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.close();
    return false;
  };

  // ─── Public methods (called via on:click declarative bindings) ───────────

  /**
   * Opens the popup dialog.
   * Wired to the float button via `on:click="/open"`.
   */
  open = () => {
    // Cancel any pending auto-open so it doesn't fire twice
    if (this.#autoOpenTimer !== null) {
      clearTimeout(this.#autoOpenTimer);
      this.#autoOpenTimer = null;
    }
    this.#showDialog();
  };

  /**
   * Closes the popup dialog and sets the dismiss cookie.
   * Wired to the close button via `on:click="/close"`.
   */
  close = () => {
    this.#setCookie(this.#cookieDays);
    this.#hideDialog();
  };

  // ─── Private helpers ────────────────────────────────────────────────────

  #showDialog() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();

      dialog.addEventListener('click', this.#handleBackdropClick);
      dialog.addEventListener('keydown', this.#handleEscapeKey);
    });
  }

  #hideDialog() {
    const dialog = this.#dialog;
    if (!dialog || !dialog.open) return;

    dialog.removeEventListener('click', this.#handleBackdropClick);
    dialog.removeEventListener('keydown', this.#handleEscapeKey);

    dialog.classList.add('dialog-closing');

    dialog.addEventListener(
      'animationend',
      () => {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

        dialog.close();
        dialog.classList.remove('dialog-closing');
      },
      { once: true }
    );
  }

  /**
   * Close the dialog when the user clicks on the `<dialog>` backdrop area.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    const dialog = this.#dialog;
    if (!dialog) return;
    const rect = dialog.getBoundingClientRect();
    const outsideX = event.clientX < rect.left || event.clientX > rect.right;
    const outsideY = event.clientY < rect.top || event.clientY > rect.bottom;

    if (outsideX || outsideY) {
      this.close();
    }
  };

  /**
   * Close the dialog on Escape key (override browser default which would call dialog.close()
   * directly, bypassing our scroll-restore & cookie logic).
   * @param {KeyboardEvent} event
   */
  #handleEscapeKey = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  /** @returns {boolean} */
  #hasCookie() {
    return document.cookie.split(';').some((c) => c.trim().startsWith(`${this.#cookieName}=`));
  }

  /** @param {number} days */
  #setCookie(days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${this.#cookieName}=1; expires=${expires}; path=/; SameSite=Lax`;
  }
}

if (!customElements.get('discount-popup-component')) {
  customElements.define('discount-popup-component', DiscountPopupComponent);
}
